import React, { useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Boxes, Layers, Mountain, Ruler, Droplets } from "lucide-react";
import { useLanguage } from "../../context/LanguageContext";

type Category = "All" | "Sand" | "Aggregate" | "Road Materials" | "Rock & Filter";

type Product = {
  title: string;
  desc: string;
  iconName: string;
  category: Category;
};

const iconMap: Record<string, any> = { Droplets, Boxes, Layers, Mountain, Ruler };

const categoryArabicLabels: Record<Category, string> = {
  All: "\u0627\u0644\u0643\u0644",
  Sand: "\u0627\u0644\u0631\u0645\u0644",
  Aggregate: "\u0627\u0644\u0631\u0643\u0627\u0645",
  "Road Materials": "\u0645\u0648\u0627\u062f \u0627\u0644\u0637\u0631\u0642",
  "Rock & Filter": "\u0627\u0644\u0635\u062e\u0648\u0631 \u0648\u0627\u0644\u0645\u0631\u0634\u062d\u0627\u062a",
};

const MiningProducts: React.FC<{ content?: any }> = ({ content }) => {
  const [cat, setCat] = useState<Category>("All");
  const { t_api } = useLanguage();

  const products: Product[] = useMemo(
    () => {
      if (content?.items && content.items.length > 0) {
        return content.items;
      }

      return [
        {
          title: "0-2 mm Manufactured Sand (Fine Aggregate)",
          desc: "Precisely graded fine material suitable for asphalt production, concrete mix optimization, block manufacturing, and specialized construction applications.",
          iconName: "Droplets",
          category: "Sand" as Category,
        },
        {
          title: "3/8 Inch (9.5 mm) Aggregates",
          desc: "High-quality crushed aggregate primarily used in asphalt surface courses, micro-surfacing, and specialized concrete products requiring fine grading and high durability.",
          iconName: "Boxes",
          category: "Aggregate" as Category,
        },
        {
          title: "3/4 Inch (19 mm) Aggregates",
          desc: "Standard construction aggregate essential for structural concrete, infrastructure projects, and road base construction, ensuring optimal load-bearing capacity.",
          iconName: "Layers",
          category: "Aggregate" as Category,
        }
      ];
    },
    [content]
  );

  const categories: Category[] = useMemo(
    () => ["All", "Sand", "Aggregate", "Road Materials", "Rock & Filter"],
    []
  );

  const filtered = useMemo(() => {
    if (cat === "All") return products;
    return products.filter((p) => p.category === cat);
  }, [cat, products]);

  const categoryLabel = (category: Category) => t_api(category, categoryArabicLabels[category]);

  return (
    <section className="bg-white py-10">
      <div className="max-w-7xl mx-auto px-4 space-y-8">
        <div className="rounded-3xl border border-gray-200 bg-gray-50 p-8 shadow-sm flex flex-col gap-6">
          <div>
            <div className="text-xs uppercase tracking-widest text-gray-500">
              Hamdan Mining
            </div>
            <h2 className="mt-2 text-3xl font-bold text-gray-900">
              {t_api("Product Portfolio (Technical Specifications)", "\u0645\u062d\u0641\u0638\u0629 \u0627\u0644\u0645\u0646\u062a\u062c\u0627\u062a (\u0627\u0644\u0645\u0648\u0627\u0635\u0641\u0627\u062a \u0627\u0644\u0641\u0646\u064a\u0629)")}
            </h2>
            <p className="mt-3 max-w-3xl text-sm text-gray-600 leading-relaxed">
              {t_api("Select a category to filter products.", "\u062d\u062f\u062f \u0641\u0626\u0629 \u0644\u062a\u0635\u0641\u064a\u0629 \u0627\u0644\u0645\u0646\u062a\u062c\u0627\u062a.")}
            </p>
          </div>

          <div className="flex flex-wrap gap-2">
            {categories.map((c) => {
              const active = c === cat;
              return (
                <button
                  key={c}
                  onClick={() => setCat(c)}
                  className={`px-5 py-2 rounded-full border text-xs md:text-sm font-bold transition-all duration-300
                    ${active
                      ? "bg-yellow-400 text-gray-950 border-yellow-400 shadow-md"
                      : "bg-white text-gray-800 border-gray-200 hover:border-yellow-400 hover:shadow-sm"
                    }`}
                >
                  {categoryLabel(c)}
                </button>
              );
            })}
          </div>
        </div>

        <div>
          <div className="rounded-3xl border bg-white shadow-sm overflow-hidden h-full flex flex-col">
            <div className="px-6 md:px-8 py-6 border-b bg-gray-50 flex items-center justify-between">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-red-500 font-bold">
                  {t_api("Catalog", "\u0627\u0644\u0643\u062a\u0627\u0644\u0648\u062c")}
                </div>
                <div className="mt-1 text-xl md:text-2xl font-bold text-gray-900 font-cambria">
                  {cat === "All" ? t_api("All Products", "\u0643\u0644 \u0627\u0644\u0645\u0646\u062a\u062c\u0627\u062a") : categoryLabel(cat)}
                </div>
              </div>
              <div className="hidden sm:block">
                <span className="bg-white px-4 py-2 rounded-xl border border-gray-100 text-xs font-bold text-gray-400">
                  {filtered.length} {t_api("Items Listed", "\u0639\u0646\u0635\u0631 \u0645\u062f\u0631\u062c")}
                </span>
              </div>
            </div>

            <div className="p-6 md:p-8 space-y-4 flex-1">
              <AnimatePresence mode="wait">
                <motion.div
                  key={cat}
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -12 }}
                  transition={{ duration: 0.3 }}
                  className="grid grid-cols-1 gap-4"
                >
                  {filtered.map((p, idx) => {
                    const Icon = iconMap[p.iconName] || Boxes;
                    return (
                      <motion.div
                        key={idx}
                        className="rounded-2xl border border-yellow-300 bg-[#fffaf0] shadow-lg shadow-gray-200/60 hover:shadow-xl transition-all p-5 group"
                        initial={{ opacity: 0, scale: 0.98 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: idx * 0.05 }}
                      >
                        <div className="flex items-start gap-5">
                          <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-orange-400 to-orange-700 text-white flex items-center justify-center shrink-0 transition-colors shadow-sm">
                            <Icon className="w-6 h-6" />
                          </div>

                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <h3 className="text-base md:text-lg font-bold text-gray-900 leading-tight">
                                {p.title}
                              </h3>
                              <span className="inline-block px-2 py-0.5 rounded text-[8px] font-black uppercase tracking-tighter bg-white text-gray-800 border border-yellow-200">
                                {categoryLabel(p.category)}
                              </span>
                            </div>
                            <p className="text-gray-600 text-sm md:text-base leading-relaxed text-justify">
                              {p.desc}
                            </p>
                          </div>
                        </div>
                      </motion.div>
                    );
                  })}
                </motion.div>
              </AnimatePresence>

              {filtered.length === 0 && (
                <div className="text-center py-20 flex flex-col items-center">
                  <div className="w-16 h-16 bg-gray-50 rounded-full flex items-center justify-center mb-4">
                    <Boxes className="w-8 h-8 text-gray-200" />
                  </div>
                  <h4 className="text-gray-400 font-bold">{t_api("No Products Found", "\u0644\u0645 \u064a\u062a\u0645 \u0627\u0644\u0639\u062b\u0648\u0631 \u0639\u0644\u0649 \u0645\u0646\u062a\u062c\u0627\u062a")}</h4>
                  <p className="text-gray-300 text-xs mt-1">{t_api("Try selecting another category or view all products.", "\u062d\u0627\u0648\u0644 \u062a\u062d\u062f\u064a\u062f \u0641\u0626\u0629 \u0623\u062e\u0631\u0649 \u0623\u0648 \u0639\u0631\u0636 \u0643\u0644 \u0627\u0644\u0645\u0646\u062a\u062c\u0627\u062a.")}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default MiningProducts;
